import type { MapLayerActions, MapLayerState } from "@/types/stores/map";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import {
  createDataLayerActions,
  createGeoJSONLayerActions,
  createGroupActions,
  createLayerRetrievalActions,
  createMapInstanceActions,
  createNativeLayerActions,
  createStaticDataActions,
  createStaticLayerActions,
  createTemporaryLayerActions,
  createUnifiedLayerActions,
} from "./actions/index";

const initialMapLayerState: MapLayerState = {
  mapInstances: {},
  activeWorkspaceId: null,
  layerVisibility: {}, // Unified layer visibility map
  staticLayers: {
    municipalities: false,
    countries: false,
    timezones: false,
  },
  dataLayers: {},
  geoJSONLayers: {},
  staticData: {},
  isLoadingStaticData: false,
  groups: {},
  unifiedGroups: {}, // Unified group state
  folderNames: {},
  temporaryLayers: {},
  textLayersVisible: false,
  roadsLayersVisible: false,
  dataLabelsVisible: true,
};

export const useMapStore = create<MapLayerState & MapLayerActions>()(
  devtools(
    (set, get) => ({
      ...initialMapLayerState,
      temporaryLayers: {},

      ...createMapInstanceActions(set, get),
      ...createStaticLayerActions(set, get),
      ...createDataLayerActions(set, get),
      ...createGeoJSONLayerActions(set, get),
      ...createStaticDataActions(set, get),
      ...createLayerRetrievalActions(set, get),
      ...createTemporaryLayerActions(set, get),
      ...createGroupActions(set, get),
      ...createUnifiedLayerActions(set, get),
      ...createNativeLayerActions(set, get),
    }),
    {
      name: "map-store",
      serialize: {
        options: {
          date: false,
          error: false,
          undefined: false,
          map: false,
          set: false,
          function: false,
        },
        refs: true,
        maxDepth: 1,
      },
      stateSanitizer: (state: MapLayerState & MapLayerActions) => ({
        ...state,
        geoJSONLayers: Object.keys(state.geoJSONLayers).reduce((acc, key) => {
          acc[key] = {
            ...state.geoJSONLayers[key],
            data: "[GeoJSON Data Hidden]",
          };
          return acc;
        }, {} as Record<string, { data: string; [key: string]: unknown }>),
        mapInstances: Object.keys(state.mapInstances).reduce((acc, key) => {
          acc[key] = "[Deck Instance]";
          return acc;
        }, {} as Record<string, string>),
        staticData: Object.keys(state.staticData).reduce((acc, key) => {
          acc[key] = "[Data Hidden]";
          return acc;
        }, {} as Record<string, string>),
        temporaryLayers: "[Layers Hidden]",
      }),
    }
  )
);

export const useStaticLayers = () => useMapStore((state) => state.staticLayers);
export const useGeoJSONLayers = () =>
  useMapStore((state) => state.geoJSONLayers);

/**
 * Hook to get group state for a specific workspace
 * @param workspaceId - Workspace identifier (can be null)
 * @returns Group state or undefined if workspaceId is null or state doesn't exist
 */
export const useGroupState = (workspaceId: string | null) =>
  useMapStore((state) => (workspaceId ? state.groups[workspaceId] : undefined));

export const useMapInstance = () => {
  const activeWorkspaceId = useMapStore((state) => state.activeWorkspaceId);
  return useMapStore((state) =>
    activeWorkspaceId
      ? state.mapInstances[activeWorkspaceId]?.deck || null
      : null
  );
};

export const useIsMapLoaded = () => {
  const activeWorkspaceId = useMapStore((state) => state.activeWorkspaceId);
  return useMapStore((state) =>
    activeWorkspaceId
      ? state.mapInstances[activeWorkspaceId]?.isLoaded || false
      : false
  );
};

export const useStaticData = () => useMapStore((state) => state.staticData);
export const useIsLoadingStaticData = () =>
  useMapStore((state) => state.isLoadingStaticData);

/**
 * Unified layer visibility hook
 * 
 * Provides a single reactive source for all layer visibility state.
 * Returns a map of layer IDs to visibility states.
 * 
 * Layer ID formats:
 * - Static layers: "static:{layerId}" (e.g., "static:municipalities")
 * - Data layers: "data:{layerId}" (e.g., "data:my-data-input")
 * - Groups: "group:{workspaceId}:{groupId}"
 * - Chunks: "chunk:{workspaceId}:{chunkId}"
 */
export const useLayerVisibility = () => {
  return useMapStore((state) => {
    const visibility: Record<string, boolean> = { ...state.layerVisibility };

    // Sync from legacy static layers
    Object.entries(state.staticLayers).forEach(([layerId, isVisible]) => {
      const unifiedId = `static:${layerId}`;
      if (!(unifiedId in visibility)) {
        visibility[unifiedId] = isVisible;
      }
    });

    // Sync from legacy data layers
    Object.entries(state.dataLayers).forEach(([layerId, isVisible]) => {
      const unifiedId = `data:${layerId}`;
      if (!(unifiedId in visibility)) {
        visibility[unifiedId] = isVisible;
      }
    });

    // Sync from geoJSON layers
    Object.entries(state.geoJSONLayers).forEach(([layerId, layerData]) => {
      const unifiedId = `data:${layerId}`;
      if (!(unifiedId in visibility)) {
        visibility[unifiedId] = layerData.isVisible ?? true;
      }
    });

    // Sync from groups (visibleGroups)
    Object.entries(state.groups).forEach(([workspaceId, groupState]) => {
      if (groupState.visibleGroups) {
        groupState.visibleGroups.forEach((groupId) => {
          const unifiedId = `group:${workspaceId}:${groupId}`;
          visibility[unifiedId] = true;
        });
      }
      // Also sync chunks
      if (groupState.chunks) {
        groupState.chunks.forEach((chunk) => {
          const unifiedId = `chunk:${workspaceId}:${chunk.value}`;
          // Chunk is visible if all its values are in visibleGroups
          const isVisible =
            chunk.values.every((value) =>
              groupState.visibleGroups?.includes(value)
            ) ?? false;
          visibility[unifiedId] = isVisible;
        });
      }
    });

    return visibility;
  });
};

/**
 * Get visibility for a specific layer ID
 */
export const useLayerVisibilityById = (layerId: string) => {
  return useMapStore((state) => {
    // Check unified map first
    if (layerId in state.layerVisibility) {
      return state.layerVisibility[layerId];
    }

    // Fall back to legacy maps
    if (layerId.startsWith("static:")) {
      const staticLayerId = layerId.replace("static:", "");
      return state.staticLayers[staticLayerId] ?? false;
    } else if (layerId.startsWith("data:")) {
      const dataLayerId = layerId.replace("data:", "");
      const dataLayerVisible = state.dataLayers[dataLayerId];
      const geoJSONLayer = state.geoJSONLayers[dataLayerId];
      return dataLayerVisible ?? geoJSONLayer?.isVisible ?? true;
    } else if (layerId.startsWith("group:") || layerId.startsWith("chunk:")) {
      const parts = layerId.split(":");
      if (parts.length >= 3) {
        const workspaceId = parts[1];
        const groupId = parts.slice(2).join(":");
        const groupState = state.groups[workspaceId];
        if (layerId.startsWith("chunk:")) {
          // For chunks, check if all values are visible
          const chunk = groupState?.chunks?.find((c) => c.value === groupId);
          if (chunk) {
            return (
              chunk.values.every((value) =>
                groupState?.visibleGroups?.includes(value)
              ) ?? false
            );
          }
        } else {
          return groupState?.visibleGroups?.includes(groupId) ?? false;
        }
      }
    }

    return false;
  });
};

// Use shallow comparison to prevent unnecessary re-renders
export const useMapActions = () =>
  useMapStore(
    useShallow((state) => ({
      setMapInstance: state.setMapInstance,
      getMapInstance: state.getMapInstance,
      getViewState: state.getViewState,
      setViewState: state.setViewState,
      removeMapInstance: state.removeMapInstance,
      setMapLoaded: state.setMapLoaded,
      setActiveWorkspace: state.setActiveWorkspace,
      setStaticLayerVisibility: state.setStaticLayerVisibility,
      toggleStaticLayer: state.toggleStaticLayer,
      setDataLayerVisibility: state.setDataLayerVisibility,
      toggleDataLayer: state.toggleDataLayer,
      addGeoJSONLayer: state.addGeoJSONLayer,
      removeGeoJSONLayer: state.removeGeoJSONLayer,
      updateGeoJSONLayerVisibility: state.updateGeoJSONLayerVisibility,
      updateGeoJSONLayerData: state.updateGeoJSONLayerData,
      setStaticData: state.setStaticData,
      setLoadingStaticData: state.setLoadingStaticData,
      getLayersForWorkspace: state.getLayersForWorkspace,
      getStaticLayers: state.getStaticLayers,
      getGeoJSONLayers: state.getGeoJSONLayers,
      getLayerVisibility: state.getLayerVisibility,
      getAllLayers: state.getAllLayers,
      clearAllLayers: state.clearAllLayers,
      addTemporaryAiMarker: state.addTemporaryAiMarker,
      removeTemporaryAiMarker: state.removeTemporaryAiMarker,
      toggleGroup: state.toggleGroup,
      setGroupColumnName: state.setGroupColumnName,
      setVisibleGroups: state.setVisibleGroups,
      setGroupConfig: state.setGroupConfig,
      getGroupConfig: state.getGroupConfig,
      updateGroupLayerFilter: state.updateGroupLayerFilter,
      getChunks: state.getChunks,
      highlightArea: state.highlightArea,
      removeHighlight: state.removeHighlight,
    }))
  );
